import { useMemo } from 'react'
import { AlertCircle, ChevronRight } from 'lucide-react'

interface HeatmapRow {
  strategy_id: string
  preset_name: string
  instrument: string
  pnl: number
  net_return: number
  win_rate: number
  max_drawdown: number
  sharpe_ratio: number | null
  profit_factor: number
  total_trades: number
}

interface MultiBacktestResults {
  multi_backtest_id: string
  strategy_id: string
  preset_name: string
  status: string
  instruments: string[]
  heatmap_data: HeatmapRow[]
  errors: Record<string, string>
  summary: Record<string, number | null>
  total_instruments: number
  completed_count: number
  failed_count: number
  is_multi_strategy?: boolean
}

interface MultiBacktestHeatmapProps {
  data: MultiBacktestResults
  onRowClick?: (strategyId: string, preset: string, instrument: string) => void
}

type MetricConfig = {
  key: keyof HeatmapRow
  label: string
  format: (v: number | null) => string
  higherIsBetter: boolean
}

const METRICS: MetricConfig[] = [
  { key: 'pnl', label: 'PnL ($)', format: v => v != null ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-', higherIsBetter: true },
  { key: 'net_return', label: 'Return %', format: v => v != null ? `${v.toFixed(2)}%` : '-', higherIsBetter: true },
  { key: 'win_rate', label: 'Win Rate', format: v => v != null ? `${v.toFixed(1)}%` : '-', higherIsBetter: true },
  { key: 'max_drawdown', label: 'Max DD', format: v => v != null ? `${v.toFixed(2)}%` : '-', higherIsBetter: true },
  { key: 'sharpe_ratio', label: 'Sharpe', format: v => v != null ? v.toFixed(2) : '-', higherIsBetter: true },
  { key: 'profit_factor', label: 'PF', format: v => v != null ? v.toFixed(2) : '-', higherIsBetter: true },
  { key: 'total_trades', label: 'Trades', format: v => v != null ? String(v) : '-', higherIsBetter: true },
]

function getCellColor(value: number | null, min: number, max: number, higherIsBetter: boolean): string {
  if (value == null || min === max) return 'transparent'
  const normalized = (value - min) / (max - min)
  const ratio = higherIsBetter ? normalized : 1 - normalized
  const hue = Math.round(ratio * 120)
  return `hsl(${hue}, 65%, 22%)`
}

export default function MultiBacktestHeatmap({ data, onRowClick }: MultiBacktestHeatmapProps) {
  const isMultiStrategy = data.is_multi_strategy || false

  // Determine which label columns to show
  const uniqueStrategies = useMemo(() => {
    const set = new Set(data.heatmap_data.map(r => r.strategy_id))
    return set.size
  }, [data.heatmap_data])

  const uniqueInstruments = useMemo(() => {
    const set = new Set(data.heatmap_data.map(r => r.instrument))
    return set.size
  }, [data.heatmap_data])

  const showStrategyCol = isMultiStrategy
  const showInstrumentCol = uniqueInstruments > 1 || !isMultiStrategy

  // Compute min/max for each metric column
  const ranges = useMemo(() => {
    const result: Record<string, { min: number; max: number }> = {}
    for (const metric of METRICS) {
      const values = data.heatmap_data
        .map(row => row[metric.key] as number | null)
        .filter((v): v is number => v != null)
      if (values.length > 0) {
        result[metric.key] = { min: Math.min(...values), max: Math.max(...values) }
      } else {
        result[metric.key] = { min: 0, max: 0 }
      }
    }
    return result
  }, [data.heatmap_data])

  // Summary row
  const summaryRow = useMemo(() => {
    if (data.heatmap_data.length === 0) return null
    const row: Record<string, number | null> = {}
    for (const metric of METRICS) {
      const values = data.heatmap_data
        .map(r => r[metric.key] as number | null)
        .filter((v): v is number => v != null)
      row[metric.key] = values.length > 0
        ? values.reduce((a, b) => a + b, 0) / values.length
        : null
    }
    return row
  }, [data.heatmap_data])

  // Sort rows by PnL descending (highest on top)
  const sortedRows = useMemo(() => {
    return [...data.heatmap_data].sort((a, b) => (b.pnl ?? 0) - (a.pnl ?? 0))
  }, [data.heatmap_data])

  // Number of label columns for colSpan on error rows
  const labelColCount = (showStrategyCol ? 1 : 0) + (showInstrumentCol ? 1 : 0)

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <h2 className="card-title">
          {isMultiStrategy ? 'Multi-Strategy Results' : 'Multi-Instrument Results'}
        </h2>
        <div className="flex items-center gap-3 text-xs text-[#888]">
          <span>{data.completed_count} completed</span>
          {data.failed_count > 0 && (
            <span className="text-red-400">{data.failed_count} failed</span>
          )}
          {isMultiStrategy ? (
            <span className="text-[#555]">{uniqueStrategies} strategies × {uniqueInstruments} instruments</span>
          ) : (
            <span className="text-[#555]">{data.strategy_id} / {data.preset_name}</span>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#333]">
              {showStrategyCol && (
                <th className="text-left p-2.5 text-[#888] font-medium whitespace-nowrap">Strategy / Preset</th>
              )}
              {showInstrumentCol && (
                <th className="text-left p-2.5 text-[#888] font-medium whitespace-nowrap">Instrument</th>
              )}
              {METRICS.map(m => (
                <th key={m.key} className="text-right p-2.5 text-[#888] font-medium whitespace-nowrap">
                  {m.label}
                </th>
              ))}
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, idx) => {
              const rowKey = `${row.strategy_id}/${row.preset_name}/${row.instrument}`
              const error = data.errors[rowKey] || data.errors[row.instrument]

              if (error) {
                return (
                  <tr key={rowKey} className="border-b border-[#222]">
                    {showStrategyCol && (
                      <td className="p-2.5 text-white text-xs">
                        <span className="font-mono">{row.strategy_id}</span>
                        <span className="text-[#666] ml-1">/ {row.preset_name}</span>
                      </td>
                    )}
                    {showInstrumentCol && (
                      <td className="p-2.5 font-mono text-white">{row.instrument}</td>
                    )}
                    <td colSpan={METRICS.length + 1} className="p-2.5">
                      <div className="flex items-center gap-2 text-red-400 text-xs">
                        <AlertCircle size={14} />
                        <span>Failed: {error}</span>
                      </div>
                    </td>
                  </tr>
                )
              }

              // Detect strategy group boundary for visual separator
              const prevRow = idx > 0 ? sortedRows[idx - 1] : null
              const isNewStrategy = isMultiStrategy && prevRow &&
                (prevRow.strategy_id !== row.strategy_id || prevRow.preset_name !== row.preset_name)

              return (
                <tr
                  key={rowKey}
                  onClick={() => onRowClick?.(row.strategy_id, row.preset_name, row.instrument)}
                  className={`border-b cursor-pointer hover:bg-[#1a1a1a] transition-colors ${
                    isNewStrategy ? 'border-t border-t-[#444] border-b-[#222]' : 'border-b-[#222]'
                  }`}
                >
                  {showStrategyCol && (
                    <td className="p-2.5 text-xs whitespace-nowrap">
                      <span className="font-mono text-white">{row.strategy_id}</span>
                      <span className="text-[#666] ml-1">/ {row.preset_name}</span>
                    </td>
                  )}
                  {showInstrumentCol && (
                    <td className="p-2.5 font-mono text-white whitespace-nowrap">{row.instrument}</td>
                  )}
                  {METRICS.map(metric => {
                    const value = row[metric.key] as number | null
                    const range = ranges[metric.key]
                    const bgColor = getCellColor(value, range.min, range.max, metric.higherIsBetter)
                    return (
                      <td
                        key={metric.key}
                        className="p-2.5 text-right font-mono text-white whitespace-nowrap"
                        style={{ backgroundColor: bgColor }}
                      >
                        {metric.format(value)}
                      </td>
                    )
                  })}
                  <td className="p-2.5 text-center">
                    <ChevronRight size={14} className="text-[#555]" />
                  </td>
                </tr>
              )
            })}

            {/* Error rows for jobs not in heatmap_data */}
            {Object.entries(data.errors).map(([key, error]) => {
              // Skip if already shown in a heatmap row
              if (data.heatmap_data.some(r => `${r.strategy_id}/${r.preset_name}/${r.instrument}` === key || r.instrument === key)) {
                return null
              }
              return (
                <tr key={key} className="border-b border-[#222]">
                  <td colSpan={labelColCount} className="p-2.5 font-mono text-white text-xs">{key}</td>
                  <td colSpan={METRICS.length + 1} className="p-2.5">
                    <div className="flex items-center gap-2 text-red-400 text-xs">
                      <AlertCircle size={14} />
                      <span>Failed: {error}</span>
                    </div>
                  </td>
                </tr>
              )
            })}

            {/* Summary row */}
            {summaryRow && (
              <tr className="border-t-2 border-[#444] bg-[#1a1a1a]">
                <td colSpan={labelColCount} className="p-2.5 text-[#888] font-medium">Average</td>
                {METRICS.map(metric => (
                  <td key={metric.key} className="p-2.5 text-right font-mono text-[#ccc] whitespace-nowrap">
                    {metric.format(summaryRow[metric.key] ?? null)}
                  </td>
                ))}
                <td></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
